import React, { useState, useMemo, useCallback } from 'react';
import { Box, Grid, Typography, IconButton } from '@material-ui/core';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { useTranslation } from 'react-i18next';
import DashboardLayout from 'layouts/Dashboard';
import theme from '../../theme';

import { getClientConfig } from './clientConfig';
import { useIndicadoresData } from './useIndicadoresData';
import { AtCircularProgress } from '../../components/AtCircularProgress';
import MetricCard from './components/MetricCard';
import IncidenciasTab from './components/IncidenciasTab';
import IndicadoresHeader from './components/IndicadoresHeader';
import { HeartbeatIcon, ClockIcon, WarningTriangleIcon, PersonasIcon, EmpresasIcon } from './icons';

function Indicadores() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [channel, setChannel] = useState('web');
  const clientConfig = getClientConfig('banco-de-chile');
  const colors = clientConfig.colors;
  const [isExpanded, setIsExpanded] = useState(false);
  const { data, loading, error } = useIndicadoresData(channel, clientConfig.canales);

  const toggleExpanded = useCallback(() => setIsExpanded(prev => !prev), []);

  const currentMonthName = useMemo(() => {
    // Use yesterday's date to match the data range (which ends at yesterday)
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const name = d.toLocaleDateString(i18n.language || 'es', { month: 'long' });
    return name.charAt(0).toUpperCase() + name.slice(1);
  }, [i18n.language]);

  const channelLabel = channel === 'web' ? 'Web' : 'App';

  const tabs = [
    { key: 'disponibilidad', icon: <HeartbeatIcon color={colors.primary} />, label: t('ind.tab.disponibilidad') },
    { key: 'tiempo', icon: <ClockIcon color={colors.primary} />, label: t('ind.tab.tiempo') },
    { key: 'incidencias', icon: <WarningTriangleIcon color={colors.primary} />, label: t('ind.tab.incidencias') },
  ];

  const renderMetricSections = (type) => {
    const sectionData = data[type];
    return (
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" mb={2}>
            <Box style={{
              width: 32, height: 32, borderRadius: 8, backgroundColor: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 8,
            }}>
              <PersonasIcon color={colors.sectionIcon} size={20} />
            </Box>
            <Typography variant="h5" style={{ fontWeight: 700 }}>{t('ind.section.personas')}</Typography>
          </Box>
          <Box style={{ backgroundColor: '#fff', borderRadius: 36, padding: '20px' }}>
            {sectionData.personas.map((obj, idx) => (
              <MetricCard
                key={obj.name}
                objective={obj}
                allObjectives={sectionData.personas}
                type={type}
                colors={colors}
                monthLabel={currentMonthName}
                locale={i18n.language || 'es'}
                isFirst={idx === 0}
                isLast={idx === sectionData.personas.length - 1}
                incidencias={data.incidencias}
              />
            ))}
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" mb={2}>
            <Box style={{
              width: 32, height: 32, borderRadius: 8, backgroundColor: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 8,
            }}>
              <EmpresasIcon color={colors.sectionIcon} size={20} />
            </Box>
            <Typography variant="h5" style={{ fontWeight: 700 }}>{t('ind.section.empresas')}</Typography>
          </Box>
          <Box style={{ backgroundColor: '#fff', borderRadius: 36, padding: '20px' }}>
            {sectionData.empresas.map((obj, idx) => (
              <MetricCard
                key={obj.name}
                objective={obj}
                allObjectives={sectionData.empresas}
                type={type}
                colors={colors}
                monthLabel={currentMonthName}
                locale={i18n.language || 'es'}
                isFirst={idx === 0}
                isLast={idx === sectionData.empresas.length - 1}
                incidencias={data.incidencias}
              />
            ))}
          </Box>
        </Grid>
      </Grid>
    );
  };

  const moduleContent = (
    <>
      {isExpanded && <IndicadoresHeader clientTheme={clientConfig} channelLabel={channelLabel} />}
      <div style={{ padding: theme.spacing(3), backgroundColor: colors.background, minHeight: '100vh' }}>
        {/* Tabs + Channel Toggle */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={3}
          style={{ backgroundColor: '#fff', borderRadius: '0', marginLeft: -theme.spacing(3), marginRight: -theme.spacing(3), marginTop: -theme.spacing(3), padding: `0 ${theme.spacing(3)}px` }}
        >
          {/* Custom tab bar */}
          <Box display="flex" alignItems="flex-end" style={{ height: 64 }}>
            {tabs.map((tab, idx) => {
              const isActive = activeTab === idx;
              return (
                <Box
                  key={tab.key}
                  onClick={() => setActiveTab(idx)}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 20px',
                    height: 48,
                    borderRadius: isActive ? '12px 12px 0 0' : 0,
                    backgroundColor: isActive ? colors.primaryLight : 'transparent',
                    borderBottom: isActive ? `2px solid ${colors.tabIndicator}` : '2px solid transparent',
                    marginBottom: 0,
                    transition: 'background-color 0.2s',
                  }}
                >
                  {tab.icon}
                  <Typography style={{
                    fontWeight: isActive ? 600 : 400,
                    fontSize: '0.875rem',
                    color: isActive ? colors.textPrimary : '#666',
                    whiteSpace: 'nowrap',
                  }}>
                    {tab.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 12 }}>
          <Box style={{
            backgroundColor: colors.toggleActiveBg,
            borderRadius: 8,
            padding: '6px 8px',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}>
            {['web', 'app'].map((ch) => {
              const isActive = channel === ch;
              return (
                <Box
                  key={ch}
                  onClick={() => setChannel(ch)}
                  style={{
                    cursor: 'pointer',
                    padding: '4px 16px',
                    borderRadius: 8,
                    backgroundColor: isActive ? '#fff' : 'transparent',
                    boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.15)' : 'none',
                    transition: 'background-color 0.2s, box-shadow 0.2s',
                  }}
                >
                  <Typography style={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: isActive ? colors.textPrimary : '#fff' ,
                  }}>
                    {ch === 'web' ? 'Web' : 'App'}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <IconButton size="small" onClick={toggleExpanded} style={{ color: colors.textPrimary }}>
            {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          </Box>
        </Box>

        {/* Tab Content */}
        {loading && <AtCircularProgress />}
        {error && (
          <Box display="flex" justifyContent="center" p={4}>
            <Typography color="error">{t('ind.error')}</Typography>
          </Box>
        )}
        {!loading && !error && data && (
          <>
            {activeTab === 0 && renderMetricSections('disponibilidad')}
            {activeTab === 1 && renderMetricSections('tiempo')}
            {activeTab === 2 && (
              <IncidenciasTab
                data={data.incidencias}
                colors={colors}
                portals={clientConfig.portals}
                locale={i18n.language || 'es'}
              />
            )}
          </>
        )}
      </div>
    </>
  );

  if (isExpanded) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 9999, backgroundColor: colors.background, overflow: 'auto',
      }}>
        {moduleContent}
      </div>
    );
  }

  return (
    <DashboardLayout title={t('sidebar.indicadores')} callbackFromParent={() => {}} isFirstTime={true}>
      {moduleContent}
    </DashboardLayout>
  );
}

export { Indicadores };
